// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  Table,
  DeleteButton,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { Tooltip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { type ComparisonAsset } from '@shared/data-access/interfaces/common/comparison-asset';
import { type FC, useMemo } from 'react';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';

export interface ComparisonTableProps {
  readonly benchmark: string;
  readonly data: ComparisonAsset[];
  readonly onRemoveTicker: (ticker: string) => void;
}

const columnHelper = createColumnHelper<ComparisonAsset>();

export const ComparisonTable: FC<ComparisonTableProps> = ({
  benchmark,
  data,
  onRemoveTicker,
}) => {
  const { t } = useTranslation();
  const { openInvestmentPopup } = useInvestmentPopup();

  const columns = useMemo(
    () => [
      columnHelper.accessor('ticker', {
        header: 'Ticker/ISIN',
        id: 'ticker',
        cell: ({ getValue, row }) => (
          <AssetBrand
            ticker={getValue()}
            assetType={row.original.assetType}
            logoUrl={row.original.url_logo}
            analyzePopup={openInvestmentPopup}
            watchlistButton={
              <WatchlistToggleButton
                ticker={getValue()}
                assetType={row.original.assetType}
              />
            }
          />
        ),
      }),
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue }) => (
          <Tooltip title={getValue()} placement="top">
            <p className="truncate uppercase">{getValue()}</p>
          </Tooltip>
        ),
        maxSize: 200,
      }),
      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        maxSize: 120,
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('returns_YTD', {
        header: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            format="percentage"
            value={getValue()}
            validate
            formatOptions={{
              signDisplay: 'exceptZero',
            }}
          />
        ),
      }),
      columnHelper.accessor('returns_1M', {
        header: t('glossary.investment-returns-m1', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            format="percentage"
            value={getValue()}
            validate
            formatOptions={{
              signDisplay: 'exceptZero',
            }}
          />
        ),
      }),
      columnHelper.accessor('returns_3M', {
        header: t('glossary.investment-returns-m3', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            format="percentage"
            value={getValue()}
            validate
            formatOptions={{
              signDisplay: 'exceptZero',
            }}
          />
        ),
      }),
      columnHelper.accessor('returns_6M', {
        header: t('glossary.investment-returns-m6', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            format="percentage"
            value={getValue()}
            validate
            formatOptions={{
              signDisplay: 'exceptZero',
            }}
          />
        ),
      }),
      columnHelper.accessor('vol', {
        header: 'Vol',
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="percentage" value={getValue()} />
        ),
      }),
      columnHelper.accessor('sharpe', {
        header: t('common.sharpe-ratio', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => <Table.Data format="decimal" value={getValue()} />,
      }),
      columnHelper.accessor('max_drawdown', {
        header: t('glossary.max-drawdown', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="percentage" value={getValue()} />
        ),
      }),
      columnHelper.accessor('ter', {
        header: t('glossary.ter', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => <Table.Data format="decimal" value={getValue()} />,
      }),
      columnHelper.display({
        id: 'options',
        enableSorting: false,
        maxSize: 100,
        cell: ({ row }) => (
          <div className="flex flex-row justify-end space-x-3.5">
            <WatchlistToggleButton
              key={row.original.ticker}
              ticker={row.original.ticker}
              assetType={row.original.assetType as 'stock'}
            />
            {row.original.ticker !== benchmark && (
              <DeleteButton
                data-testid={`remove-ticker-${row.original.ticker}`}
                onClick={() => onRemoveTicker(row.original.ticker)}
              />
            )}
          </div>
        ),
      }),
    ],
    [benchmark, onRemoveTicker, openInvestmentPopup, t],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        enablePagination
        pageSize={10}
        initialSorting={[{ id: 'ticker', desc: false }]}
        enableRowPinning
        keepPinnedRows
        initialRowPinning={{ top: ['0'] }}
      />
    </div>
  );
};
